/**
 * Invoice Utility Functions
 * 
 * Pure functions for invoice data manipulation.
 * These are extracted for testability and reusability.
 */

/**
 * Sort column types
 * IMPORTANT: Only includes fields that the API actually supports for sorting.
 * Based on Invoice model fields from API schema: https://jean-test-api.herokuapp.com/schema
 * 
 * Note: 
 * - Customer first_name/last_name are nested fields and cannot be sorted server-side.
 * - 'total' is removed because 93% of invoices have null totals in the API.
 */
export type SortColumn = 'id' | 'date' | 'deadline';
export type SortDirection = 'asc' | 'desc';

/**
 * Field mapping for API sort parameter
 * Only Invoice-level fields that have actual data are sortable
 */
const SORT_FIELD_MAP: Record<SortColumn, string> = {
  id: 'id',
  date: 'date',
  deadline: 'deadline',
};

/**
 * Build sort parameter string for API
 * Format: "+field" for ascending, "-field" for descending
 * 
 * @param column - The column to sort by
 * @param direction - The sort direction ('asc' or 'desc')
 * @returns Sort parameter string (e.g., "+date", "-total")
 */
export const buildSortParam = (column: SortColumn, direction: SortDirection): string => {
  const prefix = direction === 'asc' ? '+' : '-';
  return `${prefix}${SORT_FIELD_MAP[column]}`;
};

/**
 * Format currency amount in EUR
 * Uses French locale for proper formatting (space as thousands separator)
 * 
 * @param amount - The numeric amount to format
 * @returns Formatted currency string (e.g., "1 234,56 €")
 */
export const formatAmount = (amount: number): string => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
};

/**
 * Format date for display in DD-MM-YYYY format
 * Uses consistent European format with dash separator
 * 
 * @param dateStr - ISO date string or null
 * @returns Formatted date string or "—" for null
 */
export const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

/**
 * Invoice status types for dot indicator
 */
export type InvoiceStatus = 'paid' | 'unpaid' | 'draft';

/**
 * Get status class for the invoice dot indicator
 * 
 * @param paid - Whether the invoice is paid
 * @param finalized - Whether the invoice is finalized
 * @returns Status class string
 */
export const getInvoiceStatus = (paid: boolean, finalized: boolean): InvoiceStatus => {
  if (paid) return 'paid';
  if (finalized) return 'unpaid';
  return 'draft';
};

/**
 * Invoice line item type (minimal for calculation)
 */
interface InvoiceLineItem {
  price?: string;
  tax?: string;
}

/**
 * Calculate total from invoice lines
 * Used when invoice.total is null
 * 
 * @param lines - Array of invoice line items
 * @returns Calculated total as number
 */
export const calculateInvoiceTotal = (lines: InvoiceLineItem[]): number => {
  if (!lines || lines.length === 0) return 0;
  
  return lines.reduce((sum, line) => {
    return sum + parseFloat(line.price || '0');
  }, 0);
};

/**
 * Parse invoice total from API response
 * Handles both string total from API and calculation from lines
 * 
 * @param total - The total string from API (or null)
 * @param lines - Invoice line items for fallback calculation
 * @returns Numeric total value
 */
export const parseInvoiceTotal = (
  total: string | null,
  lines?: InvoiceLineItem[]
): number => {
  if (total) {
    return parseFloat(total);
  }
  return calculateInvoiceTotal(lines || []);
};

/**
 * Calculate tax from invoice lines
 * Used when invoice.tax is null
 * 
 * @param lines - Array of invoice line items
 * @returns Calculated tax as number
 */
export const calculateInvoiceTax = (lines: InvoiceLineItem[]): number => {
  if (!lines || lines.length === 0) return 0;
  
  return lines.reduce((sum, line) => {
    return sum + parseFloat(line.tax || '0');
  }, 0);
};

/**
 * Parse invoice tax from API response
 * Handles both string tax from API and calculation from lines
 * 
 * @param tax - The tax string from API (or null)
 * @param lines - Invoice line items for fallback calculation
 * @returns Numeric tax value
 */
export const parseInvoiceTax = (
  tax: string | null,
  lines?: InvoiceLineItem[]
): number => {
  if (tax) {
    return parseFloat(tax);
  }
  return calculateInvoiceTax(lines || []);
};

/**
 * Validate sort column
 * Only Invoice-level fields with actual data are valid
 * 
 * @param column - Column string to validate
 * @returns True if valid sort column
 */
export const isValidSortColumn = (column: string): column is SortColumn => {
  return ['id', 'date', 'deadline'].includes(column);
};

/**
 * Validate sort direction
 * 
 * @param direction - Direction string to validate
 * @returns True if valid sort direction
 */
export const isValidSortDirection = (direction: string): direction is SortDirection => {
  return direction === 'asc' || direction === 'desc';
};

